import React, { useState, useCallback } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Minus, Plus, Scale, ChevronDown, ChevronUp, Pencil, Check, X, Loader2, PlusCircle, Trash2 } from 'lucide-react';
import { useLanguage, api } from '../context/AppContext';
import { toast } from 'sonner';

// Iter. 406.fr.65 — Multi-unit support (g / ml / pièce).
// The internal amount stays in `portion_g` (legacy field name) — the `unit`
// field only affects display + AI recalculation prompt.
const UNIT_CYCLE = ['g', 'ml', 'piece'];
const UNIT_LABEL = { g: 'g', ml: 'ml', piece: 'pce' };
const UNIT_LABEL_LONG_FR = { g: 'g', ml: 'ml', piece: 'pièce(s)' };
const UNIT_LABEL_LONG_EN = { g: 'g', ml: 'ml', piece: 'unit(s)' };
// Iter. 406.fr.66 — Contextual quantity presets (Yuri "Friction UI Zéro").
// Show as chips right under the quantity row so users tap once instead of
// typing digits. Values chosen from real-world portion norms.
const QUICK_PRESETS = {
  g: [50, 100, 150, 200, 250],
  ml: [100, 250, 500, 750, 1000],
  piece: [1, 2, 3, 4, 5],
};
const cycleUnit = (u) => {
  const i = UNIT_CYCLE.indexOf(u || 'g');
  return UNIT_CYCLE[(i + 1) % UNIT_CYCLE.length];
};
const displayUnit = (u, lang) => (lang === 'fr' ? UNIT_LABEL_LONG_FR[u || 'g'] : UNIT_LABEL_LONG_EN[u || 'g']);

/**
 * FoodItemEditor - Composant réutilisable pour éditer les quantités et noms d'aliments
 * avec recalcul dynamique des macros (règle de trois ou via IA)
 * 
 * Utilisé dans: Analyse photo, Code-barres, Recettes, Description manuelle
 */
const FoodItemEditor = ({ 
  foods, 
  onFoodsChange, 
  readOnly = false,
  showDetails = true 
}) => {
  const { language } = useLanguage();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [editingNameIndex, setEditingNameIndex] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [recalculating, setRecalculating] = useState(null);
  const [addingFood, setAddingFood] = useState(false);
  const [newFoodName, setNewFoodName] = useState('');
  const [newFoodPortion, setNewFoodPortion] = useState('100');
  const [newFoodUnit, setNewFoodUnit] = useState('g');
  const [loadingNewFood, setLoadingNewFood] = useState(false);

  // Recalculate macros when quantity changes (rule of three)
  const recalculateMacros = useCallback((food, newQuantity) => {
    if (!food.base_portion_g || food.base_portion_g === 0) {
      // If no base portion, assume current values are for 100g
      food.base_portion_g = food.portion_g || 100;
      food.base_protein_g = food.protein_g || 0;
      food.base_calories = food.calories || 0;
      food.base_carbs_g = food.carbs_g || 0;
      food.base_fats_g = food.fats_g || 0;
      food.base_fiber_g = food.fiber_g || 0;
    }

    const ratio = newQuantity / food.base_portion_g;

    return {
      ...food,
      portion_g: newQuantity,
      protein_g: Math.round(food.base_protein_g * ratio * 10) / 10,
      calories: Math.round(food.base_calories * ratio),
      carbs_g: Math.round(food.base_carbs_g * ratio * 10) / 10,
      fats_g: Math.round(food.base_fats_g * ratio * 10) / 10,
      fiber_g: Math.round((food.base_fiber_g || 0) * ratio * 10) / 10,
    };
  }, []);

  // Handle quantity change
  const handleQuantityChange = (index, newQuantity) => {
    if (readOnly || newQuantity < 0) return;
    
    const updatedFoods = foods.map((food, i) => {
      if (i === index) {
        return recalculateMacros(food, newQuantity);
      }
      return food;
    });

    onFoodsChange(updatedFoods);
  };

  // Iter. 406.fr.65 — Unit toggle. Cycles g → ml → pièce and asks the AI
  // to recompute macros for the new unit (since 200 ml of Pepsi ≠ 200 g of
  // Pepsi in calories/carbs). Failure keeps the old macros — better than a
  // broken UX.
  const handleUnitToggle = async (index) => {
    if (readOnly) return;
    const current = foods[index];
    const nextUnit = cycleUnit(current.unit || 'g');
    const amount = current.portion_g || 100;
    setRecalculating(index);
    try {
      const res = await api.post('/meals/recalculate-food', {
        food_name: current.name,
        portion_g: amount,
        unit: nextUnit,
      });
      if (res.data.success && res.data.food) {
        const merged = { ...current, ...res.data.food, unit: nextUnit, portion_g: amount };
        onFoodsChange(foods.map((f, i) => (i === index ? merged : f)));
      } else {
        // Keep macros as-is, just switch the display unit
        onFoodsChange(foods.map((f, i) => (i === index ? { ...f, unit: nextUnit } : f)));
      }
    } catch {
      onFoodsChange(foods.map((f, i) => (i === index ? { ...f, unit: nextUnit } : f)));
    } finally {
      setRecalculating(null);
    }
  };

  // Quick adjust buttons (+/- 25g)
  const quickAdjust = (index, delta) => {
    const currentQuantity = foods[index].portion_g || 100;
    const newQuantity = Math.max(0, currentQuantity + delta);
    handleQuantityChange(index, newQuantity);
  };

  // Start editing a food name
  const startEditName = (index, e) => {
    e.stopPropagation();
    setEditingNameIndex(index);
    setNameInput(foods[index].name);
  };

  // Cancel name editing
  const cancelEditName = (e) => {
    e.stopPropagation();
    setEditingNameIndex(null);
    setNameInput('');
  };

  // Confirm name change and recalculate macros via AI
  const confirmNameChange = async (index, e) => {
    e.stopPropagation();
    const newName = nameInput.trim();
    if (!newName || newName === foods[index].name) {
      setEditingNameIndex(null);
      return;
    }

    setRecalculating(index);
    setEditingNameIndex(null);
    try {
      const portion = foods[index].portion_g || 100;
      const unit = foods[index].unit || 'g';
      const res = await api.post('/meals/recalculate-food', { food_name: newName, portion_g: portion, unit });
      if (res.data.success) {
        const updatedFoods = foods.map((food, i) => i === index ? { ...food, ...res.data.food } : food);
        onFoodsChange(updatedFoods);
      }
    } catch {
      // If AI lookup fails, just update the name
      const updatedFoods = foods.map((food, i) => i === index ? { ...food, name: newName } : food);
      onFoodsChange(updatedFoods);
    } finally {
      setRecalculating(null);
    }
  };

  // Add a new food item via AI lookup
  const handleAddFood = async () => {
    const name = newFoodName.trim();
    const portion = parseInt(newFoodPortion) || 100;
    const unit = newFoodUnit || 'g';
    if (!name) return;

    setLoadingNewFood(true);
    try {
      const res = await api.post('/meals/recalculate-food', { food_name: name, portion_g: portion, unit });
      if (res.data.success && res.data.food) {
        const newFood = {
          ...res.data.food,
          unit: res.data.food.unit || unit,
          base_portion_g: portion,
          base_protein_g: res.data.food.protein_g || 0,
          base_calories: res.data.food.calories || 0,
          base_carbs_g: res.data.food.carbs_g || 0,
          base_fats_g: res.data.food.fats_g || 0,
          base_fiber_g: res.data.food.fiber_g || 0,
          manually_added: true,
        };
        onFoodsChange([...foods, newFood]);
        setNewFoodName('');
        setNewFoodPortion('100');
        setNewFoodUnit('g');
        setAddingFood(false);
        toast.success(language === 'fr' ? `${newFood.name} ajoute !` : `${newFood.name} added!`);
      }
    } catch {
      // Fallback: add with just the name and 0 macros
      const fallback = {
        name,
        portion_g: portion,
        unit,
        protein_g: 0,
        calories: 0,
        carbs_g: 0,
        fats_g: 0,
        fiber_g: 0,
        base_portion_g: portion,
        base_protein_g: 0,
        base_calories: 0,
        base_carbs_g: 0,
        base_fats_g: 0,
        base_fiber_g: 0,
        manually_added: true,
      };
      onFoodsChange([...foods, fallback]);
      setNewFoodName('');
      setNewFoodPortion('100');
      setNewFoodUnit('g');
      setAddingFood(false);
      toast.info(language === 'fr' ? 'Aliment ajoute (macros a vérifier)' : 'Food added (check macros)');
    } finally {
      setLoadingNewFood(false);
    }
  };

  // Remove a food item
  const handleRemoveFood = (index) => {
    onFoodsChange(foods.filter((_, i) => i !== index));
  };

  // Calculate totals — defensive number coercion: the LLM can occasionally
  // return numeric fields as strings (e.g. "15.5"), which makes the reduce
  // concatenate strings and `totals.protein_g.toFixed()` crash the modal
  // (caught by MealAnalysisErrorBoundary as "Analyse difficile"). Force
  // every value through `safeNum` before summing.
  const safeNum = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const totals = foods.reduce((acc, food) => ({
    protein_g: acc.protein_g + safeNum(food.protein_g),
    calories: acc.calories + safeNum(food.calories),
    carbs_g: acc.carbs_g + safeNum(food.carbs_g),
    fats_g: acc.fats_g + safeNum(food.fats_g),
    fiber_g: acc.fiber_g + safeNum(food.fiber_g),
  }), { protein_g: 0, calories: 0, carbs_g: 0, fats_g: 0, fiber_g: 0 });

  return (
    <div className="space-y-3">
      {/* Food Items List — GLOW aesthetic (Iter. 411.ii) */}
      {foods.map((food, index) => {
        const rgb = food.is_processed ? '239,68,68' : food.is_super_aliment ? '251,146,60' : '148,163,184';
        return (
        <div 
          key={index}
          className="rounded-lg transition-all"
          data-testid={`food-item-${index}`}
          style={{
            background: `linear-gradient(135deg, rgba(0,0,0,0.68) 0%, rgba(15,15,15,0.62) 45%, rgba(${rgb},0.18) 100%)`,
            border: `1px solid rgba(${rgb},0.4)`,
            boxShadow: `0 0 10px rgba(${rgb},0.28), inset 0 1px 0 rgba(255,255,255,0.04)`,
            backdropFilter: 'blur(4px)',
          }}
        >
          {/* Header - Always visible */}
          <div 
            className="p-3 flex items-center gap-2 cursor-pointer"
            onClick={() => !readOnly && setExpandedIndex(expandedIndex === index ? null : index)}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
              {food.is_processed && <span className="text-destructive flex-shrink-0">⚠️</span>}
              {food.is_super_aliment && <span className="text-primary flex-shrink-0">⭐</span>}
              {editingNameIndex === index ? (
                <div className="flex items-center gap-1 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                  <Input
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmNameChange(index, e);
                      if (e.key === 'Escape') cancelEditName(e);
                    }}
                    className="h-7 text-sm bg-background flex-1"
                    autoFocus
                    data-testid={`food-name-input-${index}`}
                  />
                  <Button size="sm" variant="ghost" onClick={(e) => confirmNameChange(index, e)} className="h-7 w-7 p-0 text-primary" data-testid={`food-name-confirm-${index}`}>
                    <Check className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEditName} className="h-7 w-7 p-0 text-muted-foreground">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <>
                  {recalculating === index ? (
                    <span className="flex items-center gap-2 text-primary">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span className="text-sm">{language === 'fr' ? 'Recalcul...' : 'Recalculating...'}</span>
                    </span>
                  ) : (
                    <span className={`font-medium truncate ${food.is_processed ? 'text-destructive' : 'text-foreground'}`}>
                      {food.name}
                    </span>
                  )}
                  {!readOnly && recalculating !== index && (
                    <button
                      type="button"
                      onClick={(e) => startEditName(index, e)}
                      className="flex-shrink-0 p-1 rounded hover:bg-primary/20 text-muted-foreground hover:text-primary transition-colors"
                      title={language === 'fr' ? 'Remplacer cet aliment' : 'Replace this food'}
                      data-testid={`food-edit-name-${index}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </>
              )}
              {!readOnly && editingNameIndex !== index && (
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  ({food.portion_g || 100} {UNIT_LABEL[food.unit || 'g']})
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                <span className="text-primary font-medium">{safeNum(food.protein_g).toFixed(1)}g</span>
                <span className="hidden sm:inline"> prot</span> • {safeNum(food.calories).toFixed(0)}<span className="hidden sm:inline"> cal</span>
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemoveFood(index); }}
                  className="flex-shrink-0 p-1 rounded hover:bg-destructive/20 text-muted-foreground/40 hover:text-destructive transition-colors"
                  title={language === 'fr' ? 'Retirer' : 'Remove'}
                  data-testid={`food-remove-${index}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              {!readOnly && showDetails && (
                expandedIndex === index 
                  ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              )}
            </div>
          </div>

          {/* Expanded Editor */}
          {!readOnly && showDetails && expandedIndex === index && (
            <div className="px-3 pb-3 pt-1 border-t border-border/50 space-y-3">
              {/* Quantity Editor */}
              <div className="flex items-center gap-2">
                <Scale className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Quantité:' : 'Quantity:'}
                </span>
                <div className="flex items-center gap-1 flex-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); quickAdjust(index, -25); }}
                    className="h-8 w-8 p-0"
                    data-testid={`food-minus-${index}`}
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <Input
                    type="number"
                    value={food.portion_g || 100}
                    onChange={(e) => handleQuantityChange(index, parseFloat(e.target.value) || 0)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-8 w-20 text-center bg-background"
                    min="0"
                    step="5"
                    data-testid={`food-quantity-${index}`}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleUnitToggle(index); }}
                    disabled={recalculating === index}
                    className="h-8 min-w-[52px] px-2 rounded-md ring-1 ring-primary/40 bg-primary/10 hover:bg-primary/20 text-primary text-xs font-heading uppercase tracking-wider transition-colors disabled:opacity-50"
                    title={language === 'fr' ? 'Changer l\'unité (g ↔ ml ↔ pièce)' : 'Change unit (g ↔ ml ↔ piece)'}
                    data-testid={`food-unit-toggle-${index}`}
                  >
                    {recalculating === index ? (
                      <Loader2 className="w-3 h-3 mx-auto animate-spin" />
                    ) : (
                      displayUnit(food.unit || 'g', language)
                    )}
                  </button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={(e) => { e.stopPropagation(); quickAdjust(index, 25); }}
                    className="h-8 w-8 p-0"
                    data-testid={`food-plus-${index}`}
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Iter. 406.fr.66 — Contextual quick presets */}
              <div className="flex flex-wrap gap-1.5 pl-6" data-testid={`food-quick-presets-${index}`}>
                {(QUICK_PRESETS[food.unit || 'g']).map((preset) => {
                  const active = (food.portion_g || 100) === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleQuantityChange(index, preset); }}
                      className={`h-7 px-2.5 rounded-full text-[11px] font-heading uppercase tracking-wider transition-all ring-1 ${
                        active
                          ? 'ring-primary bg-primary text-primary-foreground'
                          : 'ring-border/60 bg-secondary hover:ring-primary/50 hover:bg-secondary/80 text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`food-preset-${index}-${preset}`}
                    >
                      {preset}{food.unit === 'piece' ? '' : ` ${UNIT_LABEL[food.unit || 'g']}`}
                    </button>
                  );
                })}
              </div>

              {/* Macros Display (read-only, auto-calculated) */}
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="bg-background/50 rounded p-2 text-center">
                  <p className="text-primary font-bold">{safeNum(food.protein_g).toFixed(1)}g</p>
                  <p className="text-muted-foreground">{language === 'fr' ? 'Prot' : 'Prot'}</p>
                </div>
                <div className="bg-background/50 rounded p-2 text-center">
                  <p className="font-bold">{safeNum(food.calories).toFixed(0)}</p>
                  <p className="text-muted-foreground">Cal</p>
                </div>
                <div className="bg-background/50 rounded p-2 text-center">
                  <p className="font-bold">{safeNum(food.carbs_g).toFixed(1)}g</p>
                  <p className="text-muted-foreground">{language === 'fr' ? 'Gluc' : 'Carbs'}</p>
                </div>
                <div className="bg-background/50 rounded p-2 text-center">
                  <p className="font-bold">{safeNum(food.fats_g).toFixed(1)}g</p>
                  <p className="text-muted-foreground">{language === 'fr' ? 'Lip' : 'Fat'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        );
      })}

      {/* Add Food Button & Form — GLOW aesthetic (Iter. 411.ii) */}
      {!readOnly && (
        <>
          {!addingFood ? (
            <button
              type="button"
              onClick={() => setAddingFood(true)}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-lg transition-all active:scale-[0.99]"
              data-testid="add-food-btn"
              style={{
                background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(15,15,15,0.5) 45%, rgba(251,146,60,0.1) 100%)',
                border: '2px dashed rgba(251,146,60,0.5)',
                boxShadow: '0 0 10px rgba(251,146,60,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <PlusCircle
                className="w-4 h-4"
                style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.85))' }}
              />
              <span
                className="text-sm font-black uppercase tracking-widest"
                style={{ color: '#fed7aa', textShadow: '0 0 5px rgba(251,146,60,0.55)' }}
              >
                {language === 'fr' ? 'Ajouter un aliment' : 'Add a food'}
              </span>
            </button>
          ) : (
            <div className="p-3 rounded-lg border-2 border-primary/30 bg-primary/5 space-y-2" data-testid="add-food-form">
              <div className="flex items-center gap-2">
                <Input
                  value={newFoodName}
                  onChange={(e) => setNewFoodName(e.target.value)}
                  placeholder={language === 'fr' ? 'Nom de l\'aliment (ex: beurre d\'arachide)' : 'Food name (e.g. peanut butter)'}
                  className="flex-1 h-9 text-sm bg-background"
                  onKeyDown={(e) => { if (e.key === 'Enter' && newFoodName.trim()) handleAddFood(); }}
                  autoFocus
                  data-testid="add-food-name-input"
                />
                <Input
                  type="number"
                  value={newFoodPortion}
                  onChange={(e) => setNewFoodPortion(e.target.value)}
                  className="w-20 h-9 text-sm text-center bg-background"
                  min="1"
                  data-testid="add-food-portion-input"
                />
                <button
                  type="button"
                  onClick={() => setNewFoodUnit(cycleUnit(newFoodUnit))}
                  className="h-9 min-w-[52px] px-2 rounded-md ring-1 ring-primary/40 bg-primary/10 hover:bg-primary/20 text-primary text-xs font-heading uppercase tracking-wider transition-colors"
                  title={language === 'fr' ? 'Changer l\'unité' : 'Change unit'}
                  data-testid="add-food-unit-toggle"
                >
                  {displayUnit(newFoodUnit, language)}
                </button>
              </div>
              {/* Iter. 406.fr.66 — Quick presets for the add-food form */}
              <div className="flex flex-wrap gap-1.5" data-testid="add-food-quick-presets">
                {QUICK_PRESETS[newFoodUnit].map((preset) => {
                  const active = String(preset) === String(newFoodPortion);
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setNewFoodPortion(String(preset))}
                      className={`h-6 px-2 rounded-full text-[10px] font-heading uppercase tracking-wider transition-all ring-1 ${
                        active
                          ? 'ring-primary bg-primary text-primary-foreground'
                          : 'ring-border/60 bg-secondary hover:ring-primary/50 text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`add-food-preset-${preset}`}
                    >
                      {preset}{newFoodUnit === 'piece' ? '' : ` ${UNIT_LABEL[newFoodUnit]}`}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddFood}
                  disabled={loadingNewFood || !newFoodName.trim()}
                  className="flex-1 h-8 bg-primary text-primary-foreground text-xs"
                  data-testid="add-food-submit-btn"
                >
                  {loadingNewFood ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PlusCircle className="w-3 h-3 mr-1" />}
                  {language === 'fr' ? 'Ajouter' : 'Add'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setAddingFood(false); setNewFoodName(''); }} className="h-8 text-xs">
                  <X className="w-3 h-3 mr-1" /> {language === 'fr' ? 'Annuler' : 'Cancel'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Totals Bar — GLOW aesthetic (Iter. 411.ii) */}
      {foods.length > 1 && (
        <div
          className="rounded-lg p-3"
          data-testid="foods-totals-bar"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(15,15,15,0.7) 45%, rgba(245,158,11,0.22) 100%)',
            border: '1.5px solid rgba(245,158,11,0.55)',
            boxShadow: '0 0 16px rgba(245,158,11,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <span
              className="font-heading uppercase text-sm tracking-widest font-black flex-shrink-0"
              style={{ color: '#fef3c7', textShadow: '0 0 6px rgba(245,158,11,0.6)' }}
            >
              {language === 'fr' ? 'Total' : 'Total'}
            </span>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm justify-end">
              <span
                className="font-black"
                style={{ color: '#fb923c', textShadow: '0 0 6px rgba(251,146,60,0.6)' }}
              >
                {totals.protein_g.toFixed(1)}g prot
              </span>
              <span className="font-black" style={{ color: '#fef3c7', textShadow: '0 0 5px rgba(245,158,11,0.5)' }}>
                {totals.calories} cal
              </span>
              <span className="text-white/70">{totals.carbs_g.toFixed(1)}g gluc</span>
              <span className="text-white/70">{totals.fats_g.toFixed(1)}g lip</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodItemEditor;
